import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';

import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementDrivers, DriverViewState } from '../../../bloc/BlocManagementDrivers';
import { Driver } from '../../../../types/cms.types';
import { CpnCreateEditDriver } from './components/CpnCreateEditDriver';


export const DriversManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementDrivers());

    // UI state
    const [selectedVehicleType, setSelectedVehicleType] = useState<string>('');
    const [selectedCity, setSelectedCity] = useState<string>('');
    const [selectedOnlineStatus, setSelectedOnlineStatus] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    // Dialog states - edit dialog and extend dialog
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showExtendDialog, setShowExtendDialog] = useState<boolean>(false);
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [newExpiryDate, setNewExpiryDate] = useState<Date | null>(null);

    // Refs
    const toast = useRef<Toast>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Load data based on active tab
    useEffect(() => {
        const statusMap = ['pending', 'approved', 'rejected'];
        const status = statusMap[activeTabIndex];
        bloc.filterByStatus(status);
    }, [activeTabIndex, bloc]);

    // Vehicle type options
    const vehicleTypeOptions = [
        { label: 'Tất cả loại xe', value: '' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' }
    ];

    // City options
    const cityOptions = [
        { label: 'Tất cả thành phố', value: '' },
        { label: 'An Giang', value: 'An Giang' },
        { label: 'Bắc Ninh', value: 'Bắc Ninh' },
        { label: 'Cà Mau', value: 'Cà Mau' },
        { label: 'Cần Thơ', value: 'Cần Thơ' },
        { label: 'Cao Bằng', value: 'Cao Bằng' },
        { label: 'Đà Nẵng', value: 'Đà Nẵng' },
        { label: 'Đắk Lắk', value: 'Đắk Lắk' },
        { label: 'Điện Biên', value: 'Điện Biên' },
        { label: 'Đồng Nai', value: 'Đồng Nai' },
        { label: 'Đồng Tháp', value: 'Đồng Tháp' },
        { label: 'Gia Lai', value: 'Gia Lai' },
        { label: 'Hà Nội', value: 'Hà Nội' },
        { label: 'Hà Tĩnh', value: 'Hà Tĩnh' },
        { label: 'Hải Phòng', value: 'Hải Phòng' },
        { label: 'TP.HCM', value: 'Hồ Chí Minh' },
        { label: 'Huế', value: 'Huế' },
        { label: 'Hưng Yên', value: 'Hưng Yên' },
        { label: 'Khánh Hoà', value: 'Khánh Hoà' },
        { label: 'Lai Châu', value: 'Lai Châu' },
        { label: 'Lâm Đồng', value: 'Lâm Đồng' },
        { label: 'Lạng Sơn', value: 'Lạng Sơn' },
        { label: 'Lào Cai', value: 'Lào Cai' },
        { label: 'Nghệ An', value: 'Nghệ An' },
        { label: 'Ninh Bình', value: 'Ninh Bình' },
        { label: 'Phú Thọ', value: 'Phú Thọ' },
        { label: 'Quảng Ngãi', value: 'Quảng Ngãi' },
        { label: 'Quảng Ninh', value: 'Quảng Ninh' },
        { label: 'Quảng Trị', value: 'Quảng Trị' },
        { label: 'Sơn La', value: 'Sơn La' },
        { label: 'Tây Ninh', value: 'Tây Ninh' },
        { label: 'Thái Nguyên', value: 'Thái Nguyên' },
        { label: 'Thanh Hoá', value: 'Thanh Hoá' },
        { label: 'Tuyên Quang', value: 'Tuyên Quang' },
        { label: 'Vĩnh Long', value: 'Vĩnh Long' }
    ];

    // Online status options
    const onlineStatusOptions = [
        { label: 'Tất cả', value: '' },
        { label: 'Online', value: 'true' },
        { label: 'Offline', value: 'false' }
    ];

    // Filter handlers
    const handleVehicleTypeFilter = async (vehicleType: string) => {
        setSelectedVehicleType(vehicleType);
        await bloc.filterByVehicleType(vehicleType);
    };

    const handleCityFilter = async (city: string) => {
        setSelectedCity(city);
        await bloc.filterByCity(city);
    };

    const handleOnlineStatusFilter = async (onlineStatus: string) => {
        setSelectedOnlineStatus(onlineStatus);
        const isOnline = onlineStatus === 'true' ? true : onlineStatus === 'false' ? false : undefined;
        await bloc.filterByOnlineStatus(isOnline);
    };

    const handleSearch = async () => {
        await bloc.searchDrivers(searchTerm);
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear filters
    const clearFilters = async () => {
        setSelectedVehicleType('');
        setSelectedCity('');
        setSelectedOnlineStatus('');
        setSearchTerm('');
        await bloc.clearFilters();
    };

    // Page change handler for DataTable's onPage event
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        // If rows changed, reset to page 1
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            // DataTable's page is 0-based, but our API expects 1-based
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refresh();
    };

    // Delete handler
    const handleDelete = (driver: Driver) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa tài xế "${driver.full_name}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.deleteDriver(driver.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã xóa tài xế thành công',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || 'Không thể xóa tài xế',
                        life: 3000
                    });
                }
            }
        });
    };

    // Status update handlers
    const handleApproveDriver = (driver: Driver) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn duyệt tài xế "${driver.full_name}"? Ngày hết hạn sẽ được đặt là 1 tháng từ ngày hiện tại.`,
            header: 'Xác nhận duyệt',
            icon: 'pi pi-question-circle',
            accept: async () => {
                try {
                    // Tính ngày hết hạn (1 tháng từ ngày hiện tại)
                    const activationDate = new Date();
                    const expiryDate = new Date();
                    expiryDate.setMonth(expiryDate.getMonth() + 1);

                    await bloc.updateDriverStatusWithDates(driver.id, 'approved', activationDate.toISOString(), expiryDate.toISOString());
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã duyệt tài xế thành công',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || 'Không thể duyệt tài xế',
                        life: 3000
                    });
                }
            }
        });
    };

    const handleRejectDriver = (driver: Driver) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn từ chối tài xế "${driver.full_name}"?`,
            header: 'Xác nhận từ chối',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.updateDriverStatusWithDates(driver.id, 'rejected');
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã từ chối tài xế thành công',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || 'Không thể từ chối tài xế',
                        life: 3000
                    });
                }
            }
        });
    };

    // Dialog handlers
    const handleEditDriver = (driver: Driver) => {
        setSelectedDriver(driver);
        setShowEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setShowEditDialog(false);
        setSelectedDriver(null);
    };

    const handleExtendDriver = (driver: Driver) => {
        setSelectedDriver(driver);
        // Set default new expiry date to 1 month from current expiry date or current date
        const currentExpiry = driver.expiry_date ? new Date(driver.expiry_date) : new Date();
        const defaultNewExpiry = new Date(currentExpiry);
        defaultNewExpiry.setMonth(defaultNewExpiry.getMonth() + 1);
        setNewExpiryDate(defaultNewExpiry);
        setShowExtendDialog(true);
    };

    const handleCloseExtendDialog = () => {
        setShowExtendDialog(false);
        setSelectedDriver(null);
        setNewExpiryDate(null);
    };

    const handleSaveExtendDate = async () => {
        if (!selectedDriver || !newExpiryDate) return;

        try {
            await bloc.updateDriverStatusWithDates(
                selectedDriver.id,
                'approved',
                selectedDriver.activation_date,
                newExpiryDate.toISOString()
            );
            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: 'Đã gia hạn tài xế thành công',
                life: 3000
            });
            handleCloseExtendDialog();
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error.message || 'Không thể gia hạn tài xế',
                life: 3000
            });
        }
    };

    const handleSaveDriver = async (driverData: any) => {
        try {
            if (selectedDriver) {
                await bloc.updateDriver(selectedDriver.id, driverData);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã cập nhật tài xế thành công',
                    life: 3000
                });
            }
            handleCloseEditDialog();
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error.message || 'Không thể cập nhật tài xế',
                life: 3000
            });
        }
    };

    // Column templates
    const driverInfoTemplate = (driver: Driver) => (
        <div>
            <div className="flex items-center gap-2">
                <div>
                    <div className="font-medium text-gray-900">{driver.full_name}</div>
                    <div className="text-sm text-gray-500">{driver.phone_number}</div>
                    {driver.email && (
                        <div className="text-sm text-gray-500">{driver.email}</div>
                    )}
                </div>
            </div>
        </div>
    );

    const vehicleInfoTemplate = (driver: Driver) => (
        <div>
            <div className="flex items-center gap-2 mb-1">
                <Tag
                    value={driver.vehicle_type === 'car' ? 'Ô tô' : 'Xe máy'}
                    severity={driver.vehicle_type === 'car' ? 'info' : 'success'}
                    icon={driver.vehicle_type === 'car' ? 'pi pi-car' : 'pi pi-truck'}
                />
            </div>
            <div className="text-sm text-gray-600">
                {driver.vehicle_type === 'car' ? driver.car_plate_number : driver.motorcycle_plate_number}
            </div>
            {driver.vehicle_type === 'car' && driver.car_brand && (
                <div className="text-sm text-gray-500">{driver.car_brand} {driver.car_model}</div>
            )}
            {driver.vehicle_type === 'motorbike' && driver.motorcycle_brand && (
                <div className="text-sm text-gray-500">{driver.motorcycle_brand} {driver.motorcycle_model}</div>
            )}
        </div>
    );

    const onlineStatusTemplate = (driver: Driver) => (
        <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${driver.is_online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className="text-sm">{driver.is_online ? 'Online' : 'Offline'}</span>
        </div>
    );

    const locationTemplate = (driver: Driver) => (
        <div className="text-sm">
            {driver.city_of_operation || 'Chưa xác định'}
        </div>
    );

    const dateTemplate = (driver: Driver) => (
        <div>
            <div className="text-sm text-gray-600">
                {new Date(driver.created_at).toLocaleDateString('vi-VN')}
            </div>

        </div>
    );

    const activationDateTemplate = (driver: Driver) => (
        <div className="text-sm">
            {driver.activation_date ? (
                <div className="text-green-600">
                    {new Date(driver.activation_date).toLocaleDateString('vi-VN')}
                </div>
            ) : (
                <span className="text-gray-400">Chưa kích hoạt</span>
            )}
        </div>
    );

    const expiryDateTemplate = (driver: Driver) => {
        if (!driver.expiry_date) {
            return <span className="text-gray-400">Chưa có</span>;
        }

        const expiryDate = new Date(driver.expiry_date);
        const today = new Date();
        const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        let statusColor = 'text-green-600';
        if (daysLeft <= 7) {
            statusColor = 'text-red-600';
        } else if (daysLeft <= 30) {
            statusColor = 'text-orange-600';
        }

        return (
            <div className="text-sm">
                <div className={statusColor}>
                    {expiryDate.toLocaleDateString('vi-VN')}
                </div>
                {daysLeft > 0 ? (
                    <div className={`text-xs ${statusColor}`}>
                        Còn {daysLeft} ngày
                    </div>
                ) : (
                    <div className="text-xs text-red-600 font-semibold">
                        Đã hết hạn
                    </div>
                )}
            </div>
        );
    };

    const actionTemplate = (driver: Driver) => (
        <div className="flex gap-1">
            <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-warning p-button-sm"
                tooltip="Chỉnh sửa thông tin"
                onClick={() => handleEditDriver(driver)}
            />
            {driver.status === 'pending' && (
                <>
                    <Button
                        icon="pi pi-check"
                        className="p-button-text p-button-success p-button-sm"
                        tooltip="Duyệt"
                        onClick={() => handleApproveDriver(driver)}
                    />
                    <Button
                        icon="pi pi-times"
                        className="p-button-text p-button-danger p-button-sm"
                        tooltip="Từ chối"
                        onClick={() => handleRejectDriver(driver)}
                    />
                </>
            )}
            {driver.status === 'approved' && (
                <>
                    <Button
                        icon="pi pi-calendar-plus"
                        className="p-button-text p-button-info p-button-sm"
                        tooltip="Gia hạn"
                        onClick={() => handleExtendDriver(driver)}
                    />
                    <Button
                        icon="pi pi-ban"
                        className="p-button-text p-button-danger p-button-sm"
                        tooltip="Khóa tài khoản"
                        onClick={() => handleRejectDriver(driver)}
                    />
                </>
            )}
            {driver.status === 'rejected' && (
                <Button
                    icon="pi pi-check"
                    className="p-button-text p-button-success p-button-sm"
                    tooltip="Kích hoạt lại"
                    onClick={() => handleApproveDriver(driver)}
                />
            )}
            <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                tooltip="Xóa"
                onClick={() => handleDelete(driver)}
            />
        </div>
    );

    // Render driver table function
    const renderDriverTable = (currentStatus: string) => {
        return (
            <StreamBuilder
                stream={bloc.getStream('info')}
                initialData={{
                    drivers: [],
                    pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                    loading: false,
                    error: null,
                    filters: {},
                    hasData: false
                }}
                builder={(snapshot: any) => {
                    const state: DriverViewState = snapshot.data;

                    if (state.error) {
                        return (
                            <div style={{ padding: '24px' }}>
                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                        <div style={{ color: '#991b1b' }}>
                                            <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                            <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // Header template with filters and search
                    const headerTemplate = (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            alignItems: 'center',
                            padding: '16px 24px',
                            borderBottom: '1px solid #e5e7eb',
                            background: '#f9fafb',
                            width: '100%'
                        }}>
                            {/* Search */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 300px', minWidth: '300px' }}>
                                <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                <InputText
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={handleSearchKeyPress}
                                    placeholder="Tìm kiếm tài xế..."
                                    style={{
                                        flex: 1,
                                        fontSize: '0.875rem',
                                        padding: '8px 12px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-search"
                                    onClick={handleSearch}
                                    className="p-button-outlined p-button-sm"
                                    style={{ padding: '6px 12px' }}
                                />
                            </div>

                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                <Button
                                    icon="pi pi-filter-slash"
                                    onClick={clearFilters}
                                    className="p-button-outlined p-button-secondary p-button-sm"
                                    tooltip="Xóa bộ lọc"
                                    style={{ padding: '6px 12px' }}
                                />
                                <Button
                                    icon="pi pi-refresh"
                                    onClick={handleRefresh}
                                    className="p-button-outlined p-button-sm"
                                    tooltip="Làm mới"
                                    style={{ padding: '6px 12px' }}
                                />
                            </div>
                        </div>
                    );

                    return (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Table */}
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                <DataTable
                                    scrollHeight='calc(100vh - 340px)'
                                    value={state.drivers}
                                    loading={state.loading || lazyLoading}
                                    loadingIcon="pi pi-spinner pi-spin"
                                    className="drivers-management-table"
                                    emptyMessage="Không tìm thấy tài xế nào"
                                    responsiveLayout="scroll"
                                    scrollable
                                    size="small"
                                    stripedRows
                                    header={headerTemplate}
                                    showGridlines
                                    paginator={state.pagination.total > 0}
                                    rows={state.pagination.limit}
                                    totalRecords={state.pagination.total}
                                    first={(state.pagination.page - 1) * state.pagination.limit}
                                    onPage={onPageChange}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                    currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} bản ghi"
                                >
                                    <Column
                                        field="driver_info"
                                        header="Thông tin tài xế"
                                        body={driverInfoTemplate}
                                        style={{ minWidth: '250px' }}
                                        frozen
                                    />
                                    <Column
                                        field="vehicle_info"
                                        header="Thông tin xe"
                                        body={vehicleInfoTemplate}
                                        style={{ minWidth: '180px' }}
                                        filter
                                        showFilterMenuOptions={false}
                                        showClearButton={false}
                                        filterElement={
                                            <Dropdown
                                                value={selectedVehicleType}
                                                options={vehicleTypeOptions}
                                                onChange={(e) => handleVehicleTypeFilter(e.value)}
                                                placeholder="Tất cả loại xe"
                                                style={{ width: '100%', fontSize: '0.75rem' }}
                                                className="p-column-filter"
                                            />
                                        }
                                    />
                                    <Column
                                        field="is_online"
                                        header="Online"
                                        body={onlineStatusTemplate}
                                        style={{ minWidth: '100px' }}
                                        filter
                                        showFilterMenuOptions={false}
                                        showClearButton={false}
                                        filterElement={
                                            <Dropdown
                                                value={selectedOnlineStatus}
                                                options={onlineStatusOptions}
                                                onChange={(e) => handleOnlineStatusFilter(e.value)}
                                                placeholder="Tất cả"
                                                style={{ width: '100%', fontSize: '0.75rem' }}
                                                className="p-column-filter"
                                            />
                                        }
                                    />
                                    <Column
                                        field="city_of_operation"
                                        header="Thành phố"
                                        body={locationTemplate}
                                        style={{ minWidth: '120px' }}
                                        filter
                                        showFilterMenuOptions={false}
                                        showClearButton={false}
                                        filterElement={
                                            <Dropdown
                                                value={selectedCity}
                                                options={cityOptions}
                                                onChange={(e) => handleCityFilter(e.value)}
                                                placeholder="Tất cả thành phố"
                                                style={{ width: '100%', fontSize: '0.75rem' }}
                                                className="p-column-filter"
                                            />
                                        }
                                    />
                                    {/* Add activation date column only for approved drivers */}
                                    {currentStatus === 'approved' && (
                                        <Column
                                            field="activation_date"
                                            header="Ngày kích hoạt"
                                            body={activationDateTemplate}
                                            style={{ minWidth: '140px' }}
                                        />
                                    )}
                                    {/* Add expiry date column only for approved drivers */}
                                    {currentStatus === 'approved' && (
                                        <Column
                                            field="expiry_date"
                                            header="Ngày hết hạn"
                                            body={expiryDateTemplate}
                                            style={{ minWidth: '140px' }}
                                        />
                                    )}
                                    <Column
                                        field="created_at"
                                        header="Ngày tạo"
                                        body={dateTemplate}
                                        style={{ minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Thao tác"
                                        body={actionTemplate}
                                        style={{ minWidth: '200px' }}
                                        frozen
                                        alignFrozen="right"
                                    />
                                </DataTable>
                            </div>

                            {/* Show message when no data and not loading */}
                            {!state.loading && state.drivers.length === 0 && (
                                <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                    <div style={{ color: '#6b7280' }}>
                                        <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '16px' }}></i>
                                        <p>Không có tài xế nào trong danh sách này</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }}
            />
        );
    };

    return (
        <AdminProtectedRoute
            requiredModule="driver_management"
            requiredSubModule="drivers"
            requiredPermission="view">
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Tab Container */}
                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <TabView
                            activeIndex={activeTabIndex}
                            onTabChange={(e) => setActiveTabIndex(e.index)}
                            style={{ height: '100%' }}
                        >
                            {/* Pending Drivers Tab */}
                            <TabPanel
                                header="Tài xế chờ duyệt"
                                leftIcon="pi pi-clock"
                                style={{ height: '100%' }}
                            >
                                {renderDriverTable('pending')}
                            </TabPanel>

                            {/* Approved Drivers Tab */}
                            <TabPanel
                                header="Tài xế đã kích hoạt"
                                leftIcon="pi pi-check-circle"
                                style={{ height: '100%' }}
                            >
                                {renderDriverTable('approved')}
                            </TabPanel>

                            {/* Rejected Drivers Tab */}
                            <TabPanel
                                header="Tài xế đã khóa"
                                leftIcon="pi pi-ban"
                                style={{ height: '100%' }}
                            >
                                {renderDriverTable('rejected')}
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </AdminLayout>

            {/* Edit Dialog */}
            <CpnCreateEditDriver
                visible={showEditDialog}
                onHide={handleCloseEditDialog}
                driver={selectedDriver}
                onSave={handleSaveDriver}
            />

            {/* Extend Dialog */}
            <Dialog
                header="Gia hạn tài xế"
                visible={showExtendDialog}
                onHide={handleCloseExtendDialog}
                style={{ width: '450px' }}
                modal
                footer={
                    <div>
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            onClick={handleCloseExtendDialog}
                            className="p-button-text"
                        />
                        <Button
                            label="Lưu"
                            icon="pi pi-check"
                            onClick={handleSaveExtendDate}
                            disabled={!newExpiryDate}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="driver-name" className="font-semibold">Tài xế:</label>
                    <p className="mt-1 text-gray-700">{selectedDriver?.full_name}</p>
                </div>

                {selectedDriver?.expiry_date && (
                    <div className="field mt-3">
                        <label htmlFor="current-expiry" className="font-semibold">Ngày hết hạn hiện tại:</label>
                        <p className="mt-1 text-gray-700">
                            {new Date(selectedDriver.expiry_date).toLocaleDateString('vi-VN')}
                        </p>
                    </div>
                )}

                <div className="field mt-3">
                    <label htmlFor="new-expiry" className="font-semibold">Ngày hết hạn mới: *</label>
                    <Calendar
                        id="new-expiry"
                        value={newExpiryDate}
                        onChange={(e) => setNewExpiryDate(e.value as Date)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        minDate={new Date()}
                        className="w-full mt-1"
                        placeholder="Chọn ngày hết hạn mới"
                    />
                </div>
            </Dialog>
        </AdminProtectedRoute>
    );
};
